import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { DocumentIcon, ListIcon, SearchIcon, ZapIcon } from '@persona-id/design-tokens/icons/16px';
import Fuse from 'fuse.js';
import React, { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { omnisearchShortcuts, resourceIconMap } from 'dashboard/components/Navbar/constants';
import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { setAndPersistCurrentEnvironment } from 'dashboard/redux/thunk-actions/current-environment';
import Input, { InputGroup } from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';

export interface OmnisearchModalProps {
  closeModal: () => void;
  openSupportChat: (initialInputText?: string) => void;
}

const OmnisearchModal = ({
  currentEnvironment,
  environments,
  closeModal,
  openSupportChat,
  setAndPersistCurrentEnvironment,
  showMessage,
}: OmnisearchModalProps & PropsFromRedux) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [personaObjectSearchResults, setPersonaObjectSearchResults] = useState<
    PersonaObjectSearchResult[]
  >([]);
  const [resourceCenterSearchResults, setResourceCenterSearchResults] = useState<
    ResourceCenterSearchResult[]
  >([]);
  const [shortcutSearchResults, setShortcutSearchResults] = useState<ShortcutSearchResult[]>([]);
  const [selectedUnifiedSearchResultIndex, setSelectedUnifiedSearchResultIndex] = useState(-1);
  const timeoutRef = useRef<number | null>(null);

  const unifiedSearchResults = useMemo(() => {
    return [
      ...personaObjectSearchResults.map((result) => ({
        'omnisearch-result-type': OmnisearchResultType.PersonaObject,
        result,
      })),
      ...shortcutSearchResults.map((result) => ({
        'omnisearch-result-type': OmnisearchResultType.Shortcut,
        result,
      })),
      ...resourceCenterSearchResults.map((result) => ({
        'omnisearch-result-type': OmnisearchResultType.ResourceCenter,
        result,
      })),
    ];
  }, [personaObjectSearchResults, resourceCenterSearchResults, shortcutSearchResults]);

  const fuse = useMemo(() => {
    return new Fuse(omnisearchShortcuts, {
      threshold: 0.3,
      ignoreLocation: true,
      keys: [{ name: 'label', weight: 1 }],
    });
  }, [omnisearchShortcuts]);

  const handleInputChange = (inputValue: string) => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }
    setSearchQuery(inputValue);

    // Debounces search to reduce frequency of API calls
    timeoutRef.current = window.setTimeout(() => {
      performSearch(inputValue);
    }, 500);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      if (selectedUnifiedSearchResultIndex < unifiedSearchResults.length - 1) {
        setSelectedUnifiedSearchResultIndex(
          (selectedUnifiedSearchResultIndex) => selectedUnifiedSearchResultIndex + 1,
        );
      }
    } else if (event.key === 'ArrowUp') {
      if (selectedUnifiedSearchResultIndex > -1) {
        setSelectedUnifiedSearchResultIndex(
          (selectedUnifiedSearchResultIndex) => selectedUnifiedSearchResultIndex - 1,
        );
      }
    } else if (event.key === 'Enter') {
      if (
        selectedUnifiedSearchResultIndex > -1 &&
        selectedUnifiedSearchResultIndex < unifiedSearchResults.length
      ) {
        const selectedUnifiedSearchResult = unifiedSearchResults[selectedUnifiedSearchResultIndex];
        if (
          selectedUnifiedSearchResult['omnisearch-result-type'] ===
          OmnisearchResultType.PersonaObject
        ) {
          if (event.ctrlKey || event.metaKey) {
            onTokenSearchResultClick(
              selectedUnifiedSearchResult.result as PersonaObjectSearchResult,
              true,
            );
          } else {
            onTokenSearchResultClick(
              selectedUnifiedSearchResult.result as PersonaObjectSearchResult,
              false,
            );
          }
        } else if (
          selectedUnifiedSearchResult['omnisearch-result-type'] ===
          OmnisearchResultType.ResourceCenter
        ) {
          onResourceCenterSearchResultClick(
            selectedUnifiedSearchResult.result as ResourceCenterSearchResult,
          );
        } else if (
          selectedUnifiedSearchResult['omnisearch-result-type'] === OmnisearchResultType.Shortcut
        ) {
          onShortcutSearchResultClick(selectedUnifiedSearchResult.result as ShortcutSearchResult);
        }
      }
    }
  };

  const performSearch = async (query: string) => {
    setPersonaObjectSearchResults([]);
    setResourceCenterSearchResults([]);
    setShortcutSearchResults([]);
    setSelectedUnifiedSearchResultIndex(-1);

    query = query.trim();
    if (query.length === 0) return;

    setIsSearchLoading(true);

    try {
      const searchResponse = await SessionClientDashboard.getEndpoint<OmnisearchResponse>(
        ApiEndpoint.Omnisearch,
        {
          query: query,
        },
      );
      setPersonaObjectSearchResults(searchResponse.data['persona-objects']);
      // Limit to the first 6 results returned by Help Center (fka Resource Center) search
      setResourceCenterSearchResults(searchResponse.data['resource-center-results'].slice(0, 6));

      if (query.length >= 3) {
        const fuseResults = fuse.search(query).map(({ item }) => item);

        // Append the "Ask AI" shortcut to the beginning of the shortcut search results,
        // only if the query doesn't look like a Persona token or an email (as there's currently
        // no reason to ask Support Chat about specific Persona objects)
        if (!isPersonaToken(query) && !EMAIL_ADDRESS_REGEX.test(query)) {
          setShortcutSearchResults([
            {
              key: 'ask-ai',
              label: (
                <>
                  <Sticker>Ask AI</Sticker>
                  {query}...
                </>
              ),
              path: '', // Instead of a path, clicking on this result will call openSupportChat
              segmentPath: 'ask-ai',
            },
            ...fuseResults,
          ]);
        } else {
          setShortcutSearchResults(fuseResults);
        }
      }
    } catch {
      // No error message needed for Forbidden
    }

    setIsSearchLoading(false);

    SegmentClient.sendSegmentTrackEvent('Omnisearch - Query', {
      searchQuery: query,
    });
  };

  const renderSearchResults = () => {
    const searchResultsHtml = [];
    let currentSearchResultIndex = -1;

    if (personaObjectSearchResults.length > 0) {
      searchResultsHtml.push(
        <>
          <SearchResultHeading>Objects</SearchResultHeading>
          {personaObjectSearchResults.map((result) => {
            currentSearchResultIndex += 1;
            const IconComponent = resourceIconMap[result['base-type']];
            return (
              <SearchResultWrapper
                key={`omnisearch-result-${result.id}`}
                onClick={(e) => onTokenSearchResultClick(result, e.ctrlKey || e.metaKey)}
                onMouseEnter={(
                  (index) => () =>
                    setSelectedUnifiedSearchResultIndex(index)
                )(currentSearchResultIndex)}
                isSelected={currentSearchResultIndex === selectedUnifiedSearchResultIndex}
              >
                <SearchResultText>
                  {IconComponent && <IconComponent />}
                  {result.id}
                </SearchResultText>
                {result['environment-name'] && <Sticker>{result['environment-name']}</Sticker>}
              </SearchResultWrapper>
            );
          })}
        </>,
      );
    }

    if (shortcutSearchResults.length > 0) {
      searchResultsHtml.push(
        <>
          <SearchResultHeading>Shortcuts</SearchResultHeading>
          {shortcutSearchResults.map((result) => {
            currentSearchResultIndex += 1;
            return (
              <SearchResultWrapper
                key={`omnisearch-result-${result.key}`}
                onClick={() => onShortcutSearchResultClick(result)}
                onMouseEnter={(
                  (index) => () =>
                    setSelectedUnifiedSearchResultIndex(index)
                )(currentSearchResultIndex)}
                isSelected={currentSearchResultIndex === selectedUnifiedSearchResultIndex}
              >
                <SearchResultText>
                  <ZapIcon />
                  {result.label}
                </SearchResultText>
              </SearchResultWrapper>
            );
          })}
        </>,
      );
    }

    if (resourceCenterSearchResults.length > 0) {
      searchResultsHtml.push(
        <>
          <SearchResultHeading>Articles</SearchResultHeading>
          {resourceCenterSearchResults.map((result) => {
            currentSearchResultIndex += 1;
            return (
              <SearchResultWrapper
                key={`omnisearch-result-${result['contentful-id']}`}
                onClick={() => onResourceCenterSearchResultClick(result)}
                onMouseEnter={(
                  (index) => () =>
                    setSelectedUnifiedSearchResultIndex(index)
                )(currentSearchResultIndex)}
                isSelected={currentSearchResultIndex === selectedUnifiedSearchResultIndex}
              >
                <SearchResultText>
                  {result['content-type'] === ResourceCenterSearchResultType.HelpCenterArticle ? (
                    <DocumentIcon />
                  ) : (
                    <ListIcon />
                  )}
                  {result.title}
                </SearchResultText>
                <Sticker>
                  {result['content-type'] === ResourceCenterSearchResultType.HelpCenterArticle
                    ? 'Article'
                    : 'Article Category'}
                </Sticker>
              </SearchResultWrapper>
            );
          })}
        </>,
      );
    }

    if (searchResultsHtml.length > 0) {
      return searchResultsHtml;
    } else if (isSearchLoading) {
      return (
        <SearchResultsCentered>
          <Paragraph color='secondary'>Loading...</Paragraph>
        </SearchResultsCentered>
      );
    } else {
      return (
        <SearchResultsCentered>
          <Paragraph color='secondary'>No results found</Paragraph>
        </SearchResultsCentered>
      );
    }
  };

  const navigate = useNavigate();

  const onShortcutSearchResultClick = (shortcutSearchResult: ShortcutSearchResult) => {
    closeModal();
    SegmentClient.sendSegmentTrackEvent('Omnisearch - Search Result Clicked', {
      resultType: 'dashboard_shortcut',
      path: shortcutSearchResult.segmentPath,
      searchQuery: searchQuery,
    });

    if (shortcutSearchResult.key === 'ask-ai') {
      openSupportChat(
        `I tried searching for "${searchQuery}" in Omnisearch, but I can’t find what I’m looking for. Can you help me with my search?`,
      );
    } else {
      navigate(shortcutSearchResult.path);
    }
  };

  const onTokenSearchResultClick = (
    tokenSearchResult: PersonaObjectSearchResult,
    openInNewTab: boolean,
  ) => {
    if (
      !tokenSearchResult['environment-id'] ||
      tokenSearchResult['environment-id'] === currentEnvironment.id
    ) {
      SegmentClient.sendSegmentTrackEvent('Omnisearch - Search Result Clicked', {
        resultType: 'token_search_result',
        path: tokenSearchResult['dashboard-path'],
        searchQuery: searchQuery,
      });

      if (openInNewTab) {
        window.open(tokenSearchResult['dashboard-path']);
      } else {
        navigate(tokenSearchResult['dashboard-path']);
        closeModal();
      }
    } else {
      const newEnv = environments.find((env) => env.id === tokenSearchResult['environment-id']);
      if (!newEnv) {
        captureMessage(
          'Omnisearch result has env that cannot be found on current user',
          'warning',
          {
            resource: tokenSearchResult.id,
            environment: tokenSearchResult['environment-id'],
          },
        );
        showMessage('Sorry, there was an issue linking to this search result', MessageType.Notice);
      } else {
        // Note: Even when the result is opened in a new tab, the environment in the current tab also
        // must be switched, due to the way the current environment is stored in Dashboard.
        setAndPersistCurrentEnvironment(newEnv);
        showMessage(`Now viewing ${newEnv.attributes.name}`, MessageType.Notice);

        SegmentClient.sendSegmentTrackEvent('Omnisearch - Search Result Clicked', {
          resultType: 'token_search_result',
          path: tokenSearchResult['dashboard-path'],
          searchQuery: searchQuery,
        });

        if (openInNewTab) {
          window.open(tokenSearchResult['dashboard-path']);
        } else {
          navigate(tokenSearchResult['dashboard-path']);
          closeModal();
        }
      }
    }
  };

  const onResourceCenterSearchResultClick = (
    resourceCenterSearchResult: ResourceCenterSearchResult,
  ) => {
    if (
      resourceCenterSearchResult['content-type'] ===
      ResourceCenterSearchResultType.HelpCenterArticle
    ) {
      const path = `https://help.withpersona.com/articles/${resourceCenterSearchResult['contentful-id']}/${resourceCenterSearchResult['slug']}`;
      SegmentClient.sendSegmentTrackEvent('Omnisearch - Search Result Clicked', {
        resultType: 'resource_center_result',
        path: path,
        searchQuery: searchQuery,
      });
      window.open(path);
    } else if (
      resourceCenterSearchResult['content-type'] === ResourceCenterSearchResultType.Category
    ) {
      const path = `https://help.withpersona.com/${resourceCenterSearchResult['slug']}`;
      SegmentClient.sendSegmentTrackEvent('Omnisearch - Search Result Clicked', {
        resultType: 'resource_center_result',
        path: path,
        searchQuery: searchQuery,
      });
      window.open(path);
    } else {
      captureMessage('Omnisearch Resource Center result has unexpected content-type', 'warning', {
        result: resourceCenterSearchResult,
      });
    }
  };

  return (
    <ModalContainer data-test='omnisearch-modal' size='lg' onKeyDown={handleKeyDown}>
      <Wrapper>
        <SearchInput>
          <InputGroup id='omnisearch-input-wrapper' size='md' leftIcon={<SearchIcon />}>
            <Input
              id='omnisearch-input'
              name='omnisearch-input'
              autoFocus
              aria-label='omnisearch-input'
              aria-describedby='omnisearch-input'
              placeholder='Search...'
              onChange={(e) => handleInputChange(e.target.value)}
              value={searchQuery}
            />
          </InputGroup>
        </SearchInput>
        <SearchResults>
          {searchQuery.length === 0 ? (
            <SearchResultsZeroState>
              <SearchResultHeading>Try searching for:</SearchResultHeading>
              <Spacer height={2} />
              <SearchResultsZeroStateExampleRow>
                <Sticker>Object tokens</Sticker>
                <>inq_WBdW3h2NX37oCur3bVaks21E</>
              </SearchResultsZeroStateExampleRow>
              <SearchResultsZeroStateExampleRow>
                <Sticker>Reference IDs</Sticker>
                <>user_519a36035c268</>
              </SearchResultsZeroStateExampleRow>
              <SearchResultsZeroStateExampleRow>
                <Sticker>Identifiers</Sticker>
                <>salesforce_id_12345</>
              </SearchResultsZeroStateExampleRow>
              <SearchResultsZeroStateExampleRow>
                <Sticker>Ask AI</Sticker>
                <>how do I...</>
              </SearchResultsZeroStateExampleRow>
              <SearchResultsZeroStateExampleRow>
                <Sticker>Help Center articles</Sticker>
                <>adverse media report</>
              </SearchResultsZeroStateExampleRow>
              <SearchResultsZeroStateExampleRow>
                <Sticker>Shortcuts</Sticker>
                <>create inquiry</>
              </SearchResultsZeroStateExampleRow>
            </SearchResultsZeroState>
          ) : (
            <>{renderSearchResults()}</>
          )}
        </SearchResults>
      </Wrapper>
    </ModalContainer>
  );
};

interface OmnisearchResponse {
  data: {
    'persona-objects': PersonaObjectSearchResult[];
    'resource-center-results': ResourceCenterSearchResult[];
  };
}

enum OmnisearchResultType {
  PersonaObject = 'persona-object',
  ResourceCenter = 'resource-center-result',
  Shortcut = 'shortcut',
}

interface PersonaObjectSearchResult {
  id: string;
  type: string;
  'base-type': ApiModel;
  'dashboard-path': string;
  'environment-id'?: string;
  'environment-name'?: string;
}

interface ResourceCenterSearchResult {
  'contentful-id': string;
  title: string;
  slug: string;
  'content-type': ResourceCenterSearchResultType;
}

enum ResourceCenterSearchResultType {
  HelpCenterArticle = 'helpCenterArticle',
  Category = 'category',
}

export interface ShortcutSearchResult {
  key: string;
  label: string | React.ReactNode;
  path: string;
  segmentPath: string; // used for Segment tracking data
}

// Note: Checks certain heuristics to determine whether the search query is likely a Persona token.
// We don't check the id length because we still don't want to show the Ask AI shortcut if the
// token is off by a character length or two.
const isPersonaToken = (input: string) => {
  const [prefix, id, shouldNotExist] = input.split('_');
  // A token should only have a prefix + id
  if (prefix == null || id == null || shouldNotExist != null) {
    return false;
  }

  if (prefix.length > 8) {
    return false;
  }

  return true;
};

const Wrapper = styled.div`
  margin-top: -${Spacing[7]};
  display: flex;
  flex-direction: column;
`;

const SearchInput = styled.div`
  padding: ${Spacing[2]} ${Spacing[1]};
  border-bottom: 1px solid ${Color.BorderDivider};

  input {
    border: 0px;

    :hover {
      box-shadow: none !important;
    }

    :focus {
      border: 0px !important;
      box-shadow: none !important;
    }
  }
`;

const SearchResults = styled.div`
  ${Typography.TextMedium};
  display: flex;
  flex-direction: column;
  row-gap: ${Spacing[2]};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const SearchResultsZeroState = styled.div`
  padding: ${Spacing[2]};
`;

const SearchResultsZeroStateExampleRow = styled.div`
  ${Typography.TextSmall};
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
  margin-bottom: ${Spacing[4]};
  color: ${Color.TextSecondary};
`;

const SearchResultsCentered = styled.div`
  padding: ${Spacing[4]};
  text-align: center;
`;

const SearchResultHeading = styled.div`
  color: ${Color.CoreSlate900};
`;

const SearchResultWrapper = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  cursor: pointer;
  border-radius: ${Spacing[1]};
  padding: ${Spacing[1]} ${Spacing[2]};
  margin-left: -${Spacing[2]};
  color: ${Color.CoreSlate1000};

  background-color: ${({ isSelected }) => (isSelected ? `${Color.CorePurple300}` : 'inherit')};
`;

const SearchResultText = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
  align-items: center;

  > svg {
    color: ${Color.BorderIconSecondary};
  }
`;

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    environments: state.organization.environments,
  };
};

const mapDispatchToProps = {
  setAndPersistCurrentEnvironment,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(OmnisearchModal);
